import React from 'react';
import type { IconType } from 'react-icons';
import {
  PiUserLight,
  PiFadersHorizontalLight,
  PiSparkleLight,
  PiBellLight,
  PiShieldCheckLight,
  PiCurrencyDollarLight,
  PiClockCounterClockwiseLight,
  PiTrashLight,
  PiBuildingLight,
  PiCreditCardLight,
  PiCurrencyCircleDollarLight,
  PiTagLight,
  PiArchiveLight,
  PiTreeStructureLight,
  PiUsersLight,
  PiLockKeyLight,
  PiListMagnifyingGlassLight,
  PiGaugeLight,
  PiChartLineUpLight,
  PiPlugsConnectedLight,
} from 'react-icons/pi';
import { isMatterEconomicsEnabled } from '../../../utils/config';
import { isAiUsageEnabled } from '../../../config/featureFlags';

/**
 * THE registry of settings sections.
 *
 * One entry per section: what it is called, where it sits, who may see it, and
 * which component draws it. `SettingsLayout` routes over this list, the left
 * rail is built from it, and the `/settings` index cards are built from it - so
 * adding a section means adding an entry here and nothing else.
 *
 * Every entry points at a component that renders THAT SECTION AND NOTHING ELSE.
 * The organization group used to point five of its ids at one
 * `OrganizationSettings` mega-component, so `/settings/org-currency` served
 * currency AND rates AND taxonomy AND retention AND tags, each under its own
 * stacked heading. That component is split; the entries below address the
 * pieces. A new section is an entry here plus one file under `sections/`.
 */

export type SettingsGroup = 'personal' | 'organization' | 'administration';

export interface SettingsRequirement {
  /** Section is only shown to organization administrators. */
  orgAdmin?: boolean;
  /**
   * A key of the license `accessRights` map (e.g. `admin_access`). Treated as
   * satisfied unless the map explicitly says `false`, so a section is never
   * hidden merely because permissions have not loaded yet.
   */
  permission?: string;
  /** Feature flag. Section is hidden entirely when this returns false. */
  flag?: () => boolean;
}

/**
 * What SHAPE a section's body is, which is the one thing the shell has to know
 * about it.
 *
 * `form` (the default) is a stack of fields: it is as tall as its content and
 * the pane scrolls it. `list` is a toolbar over a table that owns its own
 * scroller: it must be as tall as the PANE, or its pager ends up stranded
 * mid-canvas with the rest of a tall screen empty beneath it. The shell gives
 * a list section's reading column a real height (`SETTINGS_PANE_FILL_COLUMN`)
 * so the `h-full` in `LIST_SECTION_SHELL` resolves; against a content-sized
 * block it would silently become `auto`.
 */
export type SettingsSectionLayout = 'form' | 'list';

export interface SettingsSectionDef {
  /** URL segment: `/settings/<id>`. Stable - later lanes swap the body, not the id. */
  id: string;
  group: SettingsGroup;
  /** Untranslated label; run through `t()` at render. */
  label: string;
  /** Untranslated one-line description, used by the rail tooltip and the index card. */
  description: string;
  icon: IconType;
  /** Sort order within the group. */
  order: number;
  /** Body shape. Default `form`. See `SettingsSectionLayout`. */
  layout?: SettingsSectionLayout;
  requires?: SettingsRequirement;
  /**
   * Organization types that may see this section (`law_firm`, `solo_lawyer`,
   * `business`, `in_house_team`, `individual_client`). Omitted means all.
   */
  allowedOrgTypes?: string[];
  component: React.LazyExoticComponent<React.ComponentType<any>>;
  /** Extra words the settings search should match on. */
  searchKeywords?: string[];
}

export interface SettingsGroupDef {
  id: SettingsGroup;
  label: string;
  order: number;
}

export const SETTINGS_GROUPS: SettingsGroupDef[] = [
  { id: 'personal', label: 'Personal', order: 1 },
  { id: 'organization', label: 'Organization', order: 2 },
  { id: 'administration', label: 'Administration', order: 3 },
];

const NON_CLIENT_ORG_TYPES = ['law_firm', 'solo_lawyer', 'business', 'in_house_team'];

export const SETTINGS_SECTIONS: SettingsSectionDef[] = [
  // ── Personal ────────────────────────────────────────────────────────────
  {
    id: 'profile',
    group: 'personal',
    label: 'Profile',
    description: 'Your name, contact details, and public profile.',
    icon: PiUserLight,
    order: 1,
    component: React.lazy(() => import('../AccountSettings/tabs/PersonalTab')),
    searchKeywords: ['personal', 'name', 'photo', 'avatar', 'bio', 'contact'],
  },
  {
    id: 'preferences',
    group: 'personal',
    label: 'Preferences',
    description: 'Language, theme, currency, and guided tips.',
    icon: PiFadersHorizontalLight,
    order: 2,
    component: React.lazy(() => import('../SettingsTabs/GeneralSettingsTab')),
    searchKeywords: ['general', 'language', 'theme', 'dark mode', 'currency', 'default currency', 'usd', 'eur', 'tips'],
  },
  {
    id: 'ai',
    group: 'personal',
    label: 'AI',
    description: 'How the assistant behaves for you.',
    icon: PiSparkleLight,
    order: 3,
    component: React.lazy(() => import('../SettingsTabs/AISettings')),
    searchKeywords: ['assistant', 'model', 'prompt', 'suggestions'],
  },
  {
    id: 'notifications',
    group: 'personal',
    label: 'Notifications',
    description: 'What you are told about, and where.',
    icon: PiBellLight,
    order: 4,
    component: React.lazy(() => import('../SettingsTabs/NotificationsTab')),
    searchKeywords: ['email', 'push', 'alerts', 'digest'],
  },
  {
    id: 'security',
    group: 'personal',
    label: 'Security',
    description: 'Password, two-factor authentication, and signed-in devices.',
    icon: PiShieldCheckLight,
    order: 5,
    component: React.lazy(() => import('./sections/SecuritySectionBody')),
    searchKeywords: ['password', '2fa', 'mfa', 'two-factor', 'devices', 'sessions', 'verification'],
  },
  {
    id: 'my-rates',
    group: 'personal',
    label: 'My rates',
    description: 'Your own default billing rates.',
    icon: PiCurrencyDollarLight,
    order: 6,
    allowedOrgTypes: NON_CLIENT_ORG_TYPES,
    component: React.lazy(() => import('../AccountSettings/tabs/BillingRatesTab')),
    searchKeywords: ['billing', 'hourly', 'rate', 'fees'],
  },
  {
    id: 'my-activity',
    group: 'personal',
    label: 'My activity',
    description: 'A record of what you have done in the platform.',
    icon: PiClockCounterClockwiseLight,
    order: 7,
    layout: 'list',
    component: React.lazy(() => import('./sections/MyActivitySectionBody')),
    searchKeywords: ['audit', 'history', 'log', 'trail'],
  },
  {
    // Your own AI consumption. Ungated on purpose - your usage is yours to
    // see, the same way 'My activity' is. The firm-wide cut is a separate,
    // org-admin section under Organization.
    id: 'my-ai-usage',
    group: 'personal',
    label: 'My AI usage',
    description: 'The AI you have used, and what you used it on.',
    icon: PiChartLineUpLight,
    order: 8,
    /* Hidden entirely where AI usage is not released yet - no rail entry, no
       index card, no route. See `config/featureFlags`. */
    requires: { flag: isAiUsageEnabled },
    component: React.lazy(() => import('../sections/personal/MyAIUsageSection')),
    searchKeywords: ['ai usage', 'tokens', 'consumption', 'operations', 'my usage'],
  },
  {
    id: 'recycle-bin',
    group: 'personal',
    label: 'Recycle bin',
    description: 'Restore or permanently remove deleted items.',
    icon: PiTrashLight,
    order: 9,
    layout: 'list',
    component: React.lazy(() => import('../../RecycleBin/RecycleBinPage')),
    searchKeywords: ['trash', 'deleted', 'restore', 'bin'],
  },
  {
    // Per-user connections to Google Drive and OneDrive / SharePoint. What
    // lets every uploader's Files picker import from those drives (F-DMS).
    id: 'integrations',
    group: 'personal',
    label: 'Integrations',
    description: 'Connect Google Drive and SharePoint to pick documents from them.',
    icon: PiPlugsConnectedLight,
    order: 10,
    component: React.lazy(() => import('../sections/personal/IntegrationsSection')),
    searchKeywords: ['google drive', 'onedrive', 'sharepoint', 'microsoft', 'connect', 'cloud storage'],
  },

  // ── Organization ────────────────────────────────────────────────────────
  {
    id: 'org-profile',
    group: 'organization',
    label: 'Organization profile',
    description: 'Company name, registration numbers, and contact details.',
    icon: PiBuildingLight,
    order: 1,
    requires: { orgAdmin: true },
    component: React.lazy(() => import('../sections/org/OrgProfileSection')),
    searchKeywords: ['company', 'vat', 'address', 'website', 'branding', 'domain'],
  },
  {
    id: 'org-billing',
    group: 'organization',
    label: 'Billing & plan',
    description: 'Your subscription and the payment details we settle it with.',
    icon: PiCreditCardLight,
    order: 2,
    requires: { orgAdmin: true },
    component: React.lazy(() => import('../sections/org/BillingPlanSection')),
    searchKeywords: ['plan', 'subscription', 'invoice', 'card', 'iban', 'bank', 'payout'],
  },
  {
    // Currency and the rate card are ONE decision - a rate without its currency
    // is half a number - so they share a section. Renamed from `org-currency`,
    // which is why that id still resolves (see SECTION_ID_ALIASES).
    id: 'org-rates',
    group: 'organization',
    // Currency moved to Preferences (per user, feedback round 3); this page is
    // the firm's rate card only.
    label: 'Billing rates',
    description: "Each member's default hourly rate. Edit rows, then save together.",
    icon: PiCurrencyCircleDollarLight,
    order: 3,
    requires: { orgAdmin: true },
    component: React.lazy(() => import('../sections/org/RatesCurrencySection')),
    searchKeywords: [
      'money',
      'billing rates',
      'hourly',
      'rate card',
      'fees',
    ],
  },
  {
    id: 'org-tags',
    group: 'organization',
    label: 'Tags',
    description: 'Tags that can be applied to files and folders.',
    icon: PiTagLight,
    order: 4,
    requires: { orgAdmin: true },
    component: React.lazy(() => import('../sections/org/TagsSection')),
    searchKeywords: ['labels', 'classification', 'document tags'],
  },
  {
    id: 'org-retention',
    group: 'organization',
    label: 'Retention',
    description: 'How long deleted items are kept before permanent removal.',
    icon: PiArchiveLight,
    order: 5,
    requires: { orgAdmin: true },
    component: React.lazy(() => import('../sections/org/RetentionSection')),
    searchKeywords: ['auto-delete', 'recycle bin', 'trash', 'purge'],
  },
  {
    id: 'org-taxonomy',
    group: 'organization',
    label: 'Outcome taxonomy',
    description: 'The outcome categories matters are closed against.',
    icon: PiTreeStructureLight,
    order: 6,
    requires: { orgAdmin: true, flag: isMatterEconomicsEnabled },
    component: React.lazy(() => import('../sections/org/OutcomeTaxonomySection')),
    searchKeywords: ['outcomes', 'categories', 'economics', 'matter outcome'],
  },
  {
    // Usage, NEVER cost. This is the customer's own consumption readout, not
    // our economics - see `shared/aiUsage/orgAIUsageModel` for the rule.
    id: 'org-ai-usage',
    group: 'organization',
    label: 'Firm AI usage',
    description: 'How much AI your organization has used, and what it was used on.',
    icon: PiChartLineUpLight,
    order: 7,
    requires: { orgAdmin: true, flag: isAiUsageEnabled },
    component: React.lazy(() => import('../sections/org/FirmAIUsageSection')),
    searchKeywords: ['ai usage', 'tokens', 'consumption', 'operations', 'adoption'],
  },

  // ── Administration ──────────────────────────────────────────────────────
  {
    id: 'admin-overview',
    group: 'administration',
    label: 'Admin overview',
    description: 'Seats, roles, and what is waiting on you.',
    icon: PiGaugeLight,
    order: 1,
    requires: { orgAdmin: true, permission: 'admin_access' },
    component: React.lazy(() => import('../sections/admin/AdminOverviewSection')),
    searchKeywords: ['dashboard', 'summary', 'seats', 'attention', 'admin home'],
  },
  {
    id: 'admin-users',
    group: 'administration',
    label: 'Users',
    description: 'Invite, import, and manage the people in your organization.',
    icon: PiUsersLight,
    order: 2,
    layout: 'list',
    requires: { orgAdmin: true, permission: 'admin_access' },
    component: React.lazy(() => import('../../Admin/AdminUsers')),
    searchKeywords: ['people', 'invite', 'seats', 'members', 'deactivate'],
  },
  {
    id: 'admin-roles',
    group: 'administration',
    label: 'Roles & permissions',
    description: 'What each role in your organization is allowed to do.',
    icon: PiLockKeyLight,
    order: 3,
    layout: 'list',
    requires: { orgAdmin: true, permission: 'admin_access' },
    component: React.lazy(() => import('../../Admin/AdminRoles')),
    searchKeywords: ['permissions', 'access', 'role', 'rights'],
  },
  {
    id: 'admin-audit',
    group: 'administration',
    label: 'Audit log',
    description: 'Everything recorded across the organization.',
    icon: PiListMagnifyingGlassLight,
    order: 4,
    layout: 'list',
    requires: { orgAdmin: true, permission: 'admin_access' },
    component: React.lazy(() => import('./sections/AdminAuditSectionBody')),
    searchKeywords: ['audit', 'activity', 'compliance', 'history'],
  },
];

/** Who is looking - everything the visibility rules need. */
export interface SettingsViewer {
  isOrgAdmin: boolean;
  /** `userInfo.organization_type`. Null/undefined (e.g. onboarding) passes every check. */
  orgType?: string | null;
  /** The license `accessRights` map. Absent/empty grants everything. */
  accessRights?: Record<string, boolean | undefined>;
}

export const settingsSectionPath = (id: string): string => `/settings/${id}`;

/**
 * Renamed section ids, old → new.
 *
 * An id IS a route, and a route that has been shared, bookmarked or linked from
 * an email cannot simply stop existing. `getSettingsSection` resolves through
 * this map, so `/settings/org-currency` lands where currency lives now (Preferences); the
 * canonical path is also in the legacy redirect table below, so anything that
 * asks where a URL should go is told the new address rather than the old one.
 */
export const SECTION_ID_ALIASES: Record<string, string> = {
  // Currency is per user now and lives in Preferences.
  'org-currency': 'preferences',
};

/** The current id for a possibly-renamed one. Unknown ids are returned as-is. */
export const canonicalSettingsSectionId = (id: string): string => SECTION_ID_ALIASES[id] || id;

export const getSettingsSection = (id?: string | null): SettingsSectionDef | undefined => {
  if (!id) return undefined;
  const canonical = canonicalSettingsSectionId(id);
  return SETTINGS_SECTIONS.find((section) => section.id === canonical);
};

const isAllowedForOrgType = (section: SettingsSectionDef, orgType?: string | null): boolean => {
  if (!section.allowedOrgTypes || section.allowedOrgTypes.length === 0) return true;
  if (!orgType) return true; // No org yet (onboarding) → do not hide anything.
  return section.allowedOrgTypes.includes(orgType.toLowerCase());
};

/**
 * Whether a section exists at all for this viewer.
 *
 * A failed `flag` or `allowedOrgTypes` check means the section is not part of
 * this deployment/org - it should not appear anywhere, including as a
 * "not authorized" page. `orgAdmin` / `permission` are the authorization
 * checks; `SettingsLayout` distinguishes the two so a non-admin who follows a
 * bookmark gets an explanation rather than a 404.
 */
export const isSettingsSectionAvailable = (
  section: SettingsSectionDef,
  viewer: SettingsViewer
): boolean => {
  if (section.requires?.flag && !section.requires.flag()) return false;
  return isAllowedForOrgType(section, viewer.orgType);
};

/** Whether the viewer is allowed into an available section. */
export const isSettingsSectionAuthorized = (
  section: SettingsSectionDef,
  viewer: SettingsViewer
): boolean => {
  if (section.requires?.orgAdmin && !viewer.isOrgAdmin) return false;
  const permission = section.requires?.permission;
  // Explicitly denied only. An unloaded/empty rights map must not hide settings.
  if (permission && viewer.accessRights?.[permission] === false) return false;
  return true;
};

export const isSettingsSectionVisible = (
  section: SettingsSectionDef,
  viewer: SettingsViewer
): boolean => isSettingsSectionAvailable(section, viewer) && isSettingsSectionAuthorized(section, viewer);

export const visibleSettingsSections = (viewer: SettingsViewer): SettingsSectionDef[] =>
  SETTINGS_SECTIONS.filter((section) => isSettingsSectionVisible(section, viewer)).sort(
    (a, b) => a.order - b.order
  );

/** The rail: groups in order, each with its visible sections. Empty groups are dropped. */
export const visibleSettingsGroups = (
  viewer: SettingsViewer
): { group: SettingsGroupDef; sections: SettingsSectionDef[] }[] => {
  const sections = visibleSettingsSections(viewer);
  return [...SETTINGS_GROUPS]
    .sort((a, b) => a.order - b.order)
    .map((group) => ({ group, sections: sections.filter((s) => s.group === group.id) }))
    .filter((entry) => entry.sections.length > 0);
};

export const searchSettingsSections = (
  query: string,
  viewer: SettingsViewer
): SettingsSectionDef[] => {
  const needle = query.trim().toLowerCase();
  if (!needle) return visibleSettingsSections(viewer);
  return visibleSettingsSections(viewer).filter((section) =>
    [section.label, section.description, section.id, ...(section.searchKeywords || [])]
      .join(' ')
      .toLowerCase()
      .includes(needle)
  );
};

/**
 * Deep links that predate the framework.
 *
 * The old settings pages are gone as routes but not as components - every one
 * of them is a registry entry now, so an old bookmark lands on the same body at
 * its new address. Keyed by pathname, then by the legacy `?tab=` value.
 */
const LEGACY_TAB_TARGETS: Record<string, { default: string; tabs?: Record<string, string> }> = {
  '/account': {
    default: settingsSectionPath('profile'),
    tabs: {
      personal: settingsSectionPath('profile'),
      password: settingsSectionPath('security'),
      'device-management': settingsSectionPath('security'),
      'billing-rates': settingsSectionPath('my-rates'),
      activity: settingsSectionPath('my-activity'),
      // Subscription still has its own top-level page.
      plans: '/plans',
    },
  },
  '/security': { default: settingsSectionPath('security') },
  '/organization': { default: settingsSectionPath('org-profile') },
  '/document-branding': { default: settingsSectionPath('org-profile') },
  // Renamed section: Currency grew the firm-wide rate card and became
  // Rates & Currency. Old links keep working (see SECTION_ID_ALIASES) and are
  // told the canonical address here.
  '/settings/org-currency': { default: settingsSectionPath('preferences') },
  '/settings': {
    default: '/settings',
    tabs: {
      general: settingsSectionPath('preferences'),
      ai: settingsSectionPath('ai'),
      notifications: settingsSectionPath('notifications'),
      organization: settingsSectionPath('org-rates'),
      recycle_bin: settingsSectionPath('recycle-bin'),
      integrations: settingsSectionPath('integrations'),
    },
  },
};

/** Legacy paths that must be redirected. Fed straight to a `<Route path={…}>`. */
export const LEGACY_SETTINGS_PATHS = ['/account', '/security', '/organization', '/document-branding'];

/**
 * Where a legacy settings URL should land, or null when it is already correct.
 * `search` is the raw location search (`?tab=password`).
 */
export const resolveLegacySettingsTarget = (pathname: string, search = ''): string | null => {
  const entry = LEGACY_TAB_TARGETS[pathname.replace(/\/$/, '')];
  if (!entry) return null;
  const tab = new URLSearchParams(search).get('tab');
  const target = (tab && entry.tabs?.[tab]) || entry.default;
  return target === pathname ? null : target;
};
